import { useEffect, useRef, useState } from 'react';

import { TOrder } from 'types/Orders/order';
import { TCustomer } from 'types/customer';

import { CreateOrderModal } from '@organisms/orders/orderList/modals/createOrderModal/createOrderModal';
import { FillOrderModal } from '@organisms/orders/orderList/modals/fillOrderModal/fillOrderModal';

import { IncomingCallModal, TIncomingCallPayload } from './incomingCallModal';

type TProps = {
    open: boolean;
    onClose: () => void;
    /** Данные звонка от телефонии: номер, состояние линии, CRM */
    payload: TIncomingCallPayload | null;
};

/**
 * Окно входящего звонка вместе с тем, ради чего его открывают: заказом на звонящего.
 *
 * Собрано в один компонент, потому что открывают его из двух мест — реальный звонок
 * из `app.tsx` и кнопка проверки в настройках звонков, — и раньше создание заказа
 * было прицеплено только ко второму: при живом звонке «+ Заказ» молча закрывал окно.
 */
export const IncomingCallWindow = ({ open, onClose, payload }: TProps) => {
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [isActionsDropdownOpen, setIsActionsDropdownOpen] = useState(false);

    const [selectedCustomer, setSelectedCustomer] = useState<TCustomer>(null);
    const [isCreateOrderModalOpen, setIsCreateOrderModalOpen] = useState(false);
    const [orderData, setOrderData] = useState<TOrder>(null);
    const [isFillModalOpen, setIsFillModalOpen] = useState(false);

    useEffect(() => {
        if (!isActionsDropdownOpen) return undefined;

        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsActionsDropdownOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isActionsDropdownOpen]);

    /**
     * Клиент приходит найденным по номеру или только что созданным. Без него
     * (поиск ещё не закончился) мастер откроется с обычного выбора клиента.
     */
    const handleCreateOrderClick = (customer?: TCustomer) => {
        setSelectedCustomer(customer ?? null);
        setIsActionsDropdownOpen(false);
        onClose();
        setIsCreateOrderModalOpen(true);
    };

    return (
        <>
            <IncomingCallModal
                open={open}
                onClose={onClose}
                customerProfile={payload}
                onCreateOrderClick={handleCreateOrderClick}
                dropdownRef={dropdownRef}
                openActionsDropdown={isActionsDropdownOpen}
                onToggleActionsDropdown={() => setIsActionsDropdownOpen((prev) => !prev)}
                onActionClick={() => setIsActionsDropdownOpen(false)}
            />
            <CreateOrderModal
                isModalOpen={isCreateOrderModalOpen}
                setIsModalOpen={setIsCreateOrderModalOpen}
                setOrderData={setOrderData}
                onCreateOrder={() => setIsFillModalOpen(true)}
                customer={selectedCustomer}
            />
            <FillOrderModal
                isOpen={isFillModalOpen}
                onClose={() => setIsFillModalOpen(false)}
                orderData={orderData}
            />
        </>
    );
};
